/**
 * Environment utility functions
 *
 * Provides helpers for detecting the current environment based on VITE_ENVIRONMENT.
 * This is the client's dedicated environment variable, separate from PostHog configuration.
 */

/**
 * Check if the current environment is production
 *
 * Handles both 'production' and 'prod' values as they may be used interchangeably
 * depending on deployment configuration.
 *
 * @returns true if environment is production, false otherwise
 */
export function isProductionEnvironment(): boolean {
  const env = import.meta.env.VITE_ENVIRONMENT;
  return env === 'production' || env === 'prod';
}

/**
 * Check if the current environment is staging
 *
 * @returns true if environment is staging, false otherwise
 */
export function isStagingEnvironment(): boolean {
  return import.meta.env.VITE_ENVIRONMENT === 'staging';
}

/**
 * Check if the current environment is demo
 *
 * @returns true if environment is demo, false otherwise
 */
export function isDemoEnvironment(): boolean {
  return import.meta.env.VITE_ENVIRONMENT === 'demo';
}

/**
 * Check if the current environment is development/dev
 *
 * @returns true if environment is dev, false otherwise
 */
export function isDevEnvironment(): boolean {
  const env = import.meta.env.VITE_ENVIRONMENT;
  return env === 'dev' || env === 'development' || env === 'local';
}

/**
 * Get the current environment name
 *
 * @returns the current environment string or 'dev' as default
 */
export function getEnvironment(): string {
  return import.meta.env.VITE_ENVIRONMENT || 'dev';
}
